"use client";

import type { UIMessage } from "ai";
import { ArrowDownIcon, DownloadIcon } from "lucide-react";
import type { ComponentProps, MouseEvent } from "react";
import { useCallback } from "react";
import { StickToBottom, useStickToBottomContext } from "use-stick-to-bottom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type ConversationProps = ComponentProps<typeof StickToBottom>;

export const Conversation = ({ className, ...props }: ConversationProps) => (
  <StickToBottom
    data-chat-scroll-root
    className={cn(
      "relative flex-1 overflow-y-auto [scrollbar-gutter:stable_both-edges]",
      className,
    )}
    initial="instant"
    resize="smooth"
    role="log"
    {...props}
  />
);

export type ConversationContentProps = ComponentProps<
  typeof StickToBottom.Content
>;

export const ConversationContent = ({
  className,
  onClickCapture,
  scrollClassName,
  ...props
}: ConversationContentProps) => {
  const { stopScroll } = useStickToBottomContext();

  const handleClickCapture = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      onClickCapture?.(event);
      if (event.defaultPrevented || !(event.target instanceof Element)) {
        return;
      }

      const disclosureTrigger = event.target.closest<HTMLElement>(
        "[data-preserve-chat-scroll-on-expand]",
      );
      if (disclosureTrigger?.getAttribute("aria-expanded") === "false") {
        // 手動展開はストリーム更新ではないため、現在位置を保って内容を読めるよう自動追従を解除する。
        stopScroll();
      }
    },
    [onClickCapture, stopScroll],
  );

  return (
    <StickToBottom.Content
      className={cn("p-2.5 sm:p-3", className)}
      onClickCapture={handleClickCapture}
      scrollClassName={cn("chat-scroll-viewport", scrollClassName)}
      {...props}
    />
  );
};

export type ConversationScrollButtonProps = ComponentProps<typeof Button>;

export const ConversationScrollButton = ({
  className,
  ...props
}: ConversationScrollButtonProps) => {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  const handleScrollToBottom = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  return (
    !isAtBottom && (
      <Button
        className={cn(
          "absolute bottom-4 left-[50%] translate-x-[-50%] rounded-full dark:bg-background dark:hover:bg-muted",
          className,
        )}
        onClick={handleScrollToBottom}
        size="icon"
        type="button"
        variant="outline"
        {...props}
      >
        <ArrowDownIcon className="size-4" />
      </Button>
    )
  );
};

const getMessageText = (message: UIMessage): string =>
  message.parts
    .filter((part) => part.type === "text")
    .map((part) => part.text)
    .join("");

type ConversationDownloadProps = Omit<
  ComponentProps<typeof Button>,
  "onClick"
> & {
  messages: UIMessage[];
  filename?: string;
  formatMessage?: (message: UIMessage, index: number) => string;
};

const defaultFormatMessage = (message: UIMessage): string => {
  const roleLabel =
    message.role.charAt(0).toUpperCase() + message.role.slice(1);
  return `**${roleLabel}:** ${getMessageText(message)}`;
};

/** AI Elements 標準の会話エクスポート形式へ変換する。 */
export const messagesToMarkdown = (
  messages: UIMessage[],
  formatMessage: (
    message: UIMessage,
    index: number,
  ) => string = defaultFormatMessage,
): string =>
  messages.map((message, index) => formatMessage(message, index)).join("\n\n");

/** 会話を Markdown ファイルとしてローカルへ保存する。 */
const _ConversationDownload = ({
  messages,
  filename = "conversation.md",
  formatMessage = defaultFormatMessage,
  className,
  children,
  ...props
}: ConversationDownloadProps) => {
  const handleDownload = useCallback(() => {
    const markdown = messagesToMarkdown(messages, formatMessage);
    const blob = new Blob([markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [messages, filename, formatMessage]);

  return (
    <Button
      className={cn(
        "absolute top-4 right-4 rounded-full dark:bg-background dark:hover:bg-muted",
        className,
      )}
      onClick={handleDownload}
      size="icon"
      type="button"
      variant="outline"
      {...props}
    >
      {children ?? <DownloadIcon className="size-4" />}
    </Button>
  );
};
